import React from 'react';
import type { Plant } from '../../../../types/plant';
import { Leaf, Clock, Shield } from 'lucide-react';

interface VarietiesProps {
  plant: Plant;
}

export default function Varieties({ plant }: VarietiesProps) {
  if (!plant.varieties?.length) {
    return (
      <section className="bg-white rounded-lg shadow-sm p-6">
        <h2 className="text-2xl font-semibold mb-6">Varieties</h2>
        <p className="text-gray-600">No variety information available for this plant.</p>
      </section>
    );
  }

  return (
    <section className="bg-white rounded-lg shadow-sm p-6">
      <h2 className="text-2xl font-semibold mb-6">Varieties</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {plant.varieties.map((variety) => (
          <div 
            key={variety.id}
            className="border rounded-lg overflow-hidden hover:shadow-md transition-shadow"
          >
            {variety.image_url && (
              <img 
                src={variety.image_url} 
                alt={variety.name}
                className="w-full h-48 object-cover"
              />
            )}
            <div className="p-4">
              <h3 className="font-semibold text-lg mb-2">{variety.name}</h3>
              <p className="text-gray-600 mb-3">{variety.description}</p>
              
              <div className="space-y-2">
                {variety.days_to_maturity && (
                  <div className="flex items-center gap-2 text-sm">
                    <Clock className="h-4 w-4 text-primary-500" />
                    <span>Days to maturity: {variety.days_to_maturity}</span>
                  </div>
                )}
                
                {variety.unique_features && (
                  <div className="flex items-center gap-2 text-sm">
                    <Leaf className="h-4 w-4 text-primary-500" />
                    <span>{variety.unique_features}</span>
                  </div>
                )}
              </div>
              
              {variety.disease_resistance?.length > 0 && (
                <div className="mt-3">
                  <div className="flex items-center gap-2 mb-2 text-sm">
                    <Shield className="h-4 w-4 text-primary-500" />
                    <span className="font-medium">Disease Resistance</span>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {variety.disease_resistance.map((resistance, i) => (
                      <span 
                        key={i}
                        className="px-2 py-1 bg-primary-50 text-primary-700 text-xs rounded-full"
                      >
                        {resistance}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}